/**
 * The "Stopped" chip (task 1164) — beside an AI Service's name on its page, in
 * the AI Services list and in every picker that lists services.
 *
 * Amber is a stop the owner chose; red is the circuit breaker, because the
 * service is failing rather than paused. It is a warning, never a blocker: a
 * stopped service stays selectable wherever it is listed, since a stop is
 * temporary and whatever points at the service may outlive it.
 *
 * Renders nothing while the service is taking calls (`stop` null or absent —
 * absent on innovate-service builds that predate task 1163).
 */
import { useFlag } from '@/hooks/useFeatureFlags';
import type { EndpointStopKind, EndpointStopSummary } from '@/types';

/** The chip's words, by kind. Also the text a plain `<option>` label carries. */
export const SERVICE_STOP_LABELS: Record<EndpointStopKind, string> = {
  manual: 'Stopped',
  auto: 'Stopped automatically',
};

const CHIP_CLASSES: Record<EndpointStopKind, string> = {
  manual: 'bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-200',
  auto: 'bg-red-100 text-red-800 dark:bg-red-900/60 dark:text-red-200',
};

/** A text-only label for controls that cannot hold a chip (a `<select>` option). */
export const withStopLabel = (label: string, stop: EndpointStopSummary | null | undefined): string =>
  stop ? `${label} (${SERVICE_STOP_LABELS[stop.kind]})` : label;

interface ServiceStopChipProps {
  stop: EndpointStopSummary | null | undefined;
  className?: string;
}

export function ServiceStopChip({ stop, className = '' }: ServiceStopChipProps) {
  // Stop and Start's lever (task 1216) hides the chip with the actions.
  const stopEnabled = useFlag('innovate.fe.services.stop');
  if (!stop || !stopEnabled) return null;
  return (
    <span
      data-testid="service-stop-chip"
      data-kind={stop.kind}
      className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${CHIP_CLASSES[stop.kind]} ${className}`}
    >
      {SERVICE_STOP_LABELS[stop.kind]}
    </span>
  );
}

export default ServiceStopChip;
